'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, forwardRef, useCallback, useContext, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, useSyncExternalStore, } from 'react';
import { cx } from '../../../internal/cx.js';
import { ChevronRightGlyph, ColumnsGlyph } from '../../../internal/icons.js';
import { Button, IconButton, } from '../../../components/actions/button/index.js';
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle, } from '../../../components/overlays/sheet/index.js';
/* ------------------------------------------------------------------ */
/* Breakpoints                                                         */
/* ------------------------------------------------------------------ */
// Kept in sync with split-inbox-layout.css. Below 768px one pane shows at a
// time; below 1280px the context pane is a drawer instead of a column.
const SINGLE_QUERY = '(max-width: 767px)';
const DRAWER_QUERY = '(max-width: 1279px)';
function mediaStore(query) {
    const get = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(query).matches
        : false;
    const subscribe = (notify) => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return () => { };
        const mql = window.matchMedia(query);
        mql.addEventListener?.('change', notify);
        return () => mql.removeEventListener?.('change', notify);
    };
    return { get, subscribe };
}
const single = mediaStore(SINGLE_QUERY);
const drawer = mediaStore(DRAWER_QUERY);
const serverFalse = () => false;
const LayoutContext = createContext(null);
/**
 * Reads the nearest SplitInboxLayout from inside one of its panes, for
 * custom pane toggles (a focus-mode button, a details button). Returns
 * `null` outside a layout.
 */
export function useSplitInboxLayout() {
    return useContext(LayoutContext);
}
/**
 * The button that shows and hides the context pane, for the detail pane's
 * header. It toggles the column on wide screens and the drawer otherwise,
 * and exposes the state with `aria-expanded`.
 */
export const SplitInboxContextToggle = forwardRef(function SplitInboxContextToggle({ showLabel = 'Show details', hideLabel = 'Hide details', icon, onClick, ...rest }, ref) {
    const layout = useSplitInboxLayout();
    const visible = layout?.contextVisible ?? false;
    return (_jsx(IconButton, { ref: ref, ...rest, label: visible ? hideLabel : showLabel, icon: icon ?? _jsx(ColumnsGlyph, {}), "aria-expanded": visible, "aria-controls": visible && layout?.contextMode === 'inline' ? layout.ids.context : undefined, onClick: (event) => {
            onClick?.(event);
            if (!event.defaultPrevented)
                layout?.toggleContext();
        } }));
});
function isRtl(el) {
    return typeof window !== 'undefined' && window.getComputedStyle(el).direction === 'rtl';
}
function Splitter({ label, controls, size, min, max, step, collapsed, edge, onSize, onToggle, }) {
    const drag = useRef(null);
    const clamp = (n) => Math.min(max, Math.max(min, Math.round(n)));
    const onKeyDown = (event) => {
        const big = event.shiftKey ? step * 4 : step;
        // Arrow keys move the splitter, so the pane grows towards the arrow for the
        // list (start edge) and away from it for the context pane (end edge).
        const sign = (edge === 'start' ? 1 : -1) * (isRtl(event.currentTarget) ? -1 : 1);
        let next = null;
        switch (event.key) {
            case 'ArrowRight':
                next = size + big * sign;
                break;
            case 'ArrowLeft':
                next = size - big * sign;
                break;
            case 'Home':
                next = min;
                break;
            case 'End':
                next = max;
                break;
            case 'Enter':
                event.preventDefault();
                onToggle();
                return;
            default:
                return;
        }
        event.preventDefault();
        if (collapsed)
            onToggle();
        onSize(clamp(next));
    };
    const onPointerDown = (event) => {
        if (event.button !== 0)
            return;
        event.preventDefault();
        if (collapsed) {
            onToggle();
            return;
        }
        const sign = (edge === 'start' ? 1 : -1) * (isRtl(event.currentTarget) ? -1 : 1);
        drag.current = { x: event.clientX, size, sign };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        event.currentTarget.focus({ preventScroll: true });
    };
    const onPointerMove = (event) => {
        const d = drag.current;
        if (!d)
            return;
        onSize(clamp(d.size + (event.clientX - d.x) * d.sign));
    };
    const onPointerEnd = (event) => {
        if (!drag.current)
            return;
        drag.current = null;
        event.currentTarget.releasePointerCapture?.(event.pointerId);
    };
    return (
    // A focusable separator is the APG window-splitter widget; jsx-a11y treats
    // every separator as static.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    _jsx("div", { role: "separator", 
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex: 0, "aria-orientation": "vertical", "aria-label": label, "aria-controls": controls, "aria-valuemin": min, "aria-valuemax": max, "aria-valuenow": collapsed ? min : size, "data-collapsed": collapsed || undefined, "data-edge": edge, className: "mn-split-inbox__splitter", onKeyDown: onKeyDown, onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: onPointerEnd, onPointerCancel: onPointerEnd }));
}
/* ------------------------------------------------------------------ */
/* Layout                                                              */
/* ------------------------------------------------------------------ */
function useControllable(value, initial, onChange) {
    const [inner, setInner] = useState(initial);
    const controlled = value !== undefined;
    const current = controlled ? value : inner;
    const set = useCallback((next) => {
        if (!controlled)
            setInner(next);
        onChange?.(next);
    }, [controlled, onChange]);
    return [current, set];
}
/**
 * The one-page inbox: list, detail and an optional context pane. Panes are
 * resizable with the pointer and the keyboard and collapsible; below 768px
 * it becomes one pane with back navigation, and below 1280px the context pane
 * becomes a drawer. F6 and Shift+F6 move focus between panes. The product
 * supplies every pane's content and owns the URL state.
 */
export const SplitInboxLayout = forwardRef(function SplitInboxLayout({ list, detail, context, listLabel = 'List', detailLabel = 'Detail', contextLabel = 'Details', detailOpen = false, onBack, backLabel = 'Back to list', listSize: listSizeProp, defaultListSize = 360, onListSizeChange, minListSize = 240, maxListSize = 560, listCollapsed: listCollapsedProp, defaultListCollapsed = false, onListCollapsedChange, contextSize: contextSizeProp, defaultContextSize = 340, onContextSizeChange, minContextSize = 260, maxContextSize = 520, contextOpen: contextOpenProp, defaultContextOpen = true, onContextOpenChange, contextCloseLabel = 'Close details', resizable = true, resizeStep = 16, listResizeLabel = 'Resize list', contextResizeLabel = 'Resize details', className, style, onKeyDown, ...rest }, ref) {
    const rootRef = useRef(null);
    useImperativeHandle(ref, () => rootRef.current);
    const base = useId();
    const ids = useMemo(() => ({
        list: `${base}-list`,
        detail: `${base}-detail`,
        context: `${base}-context`,
    }), [base]);
    const isSingle = useSyncExternalStore(single.subscribe, single.get, serverFalse);
    const isDrawer = useSyncExternalStore(drawer.subscribe, drawer.get, serverFalse);
    const layout = isSingle ? 'single' : 'split';
    const contextMode = isDrawer ? 'drawer' : 'inline';
    const [listSize, setListSize] = useControllable(listSizeProp, defaultListSize, onListSizeChange);
    const [listCollapsed, setListCollapsed] = useControllable(listCollapsedProp, defaultListCollapsed, onListCollapsedChange);
    const [contextSize, setContextSize] = useControllable(contextSizeProp, defaultContextSize, onContextSizeChange);
    const [contextOpen, setContextOpen] = useControllable(contextOpenProp, defaultContextOpen, onContextOpenChange);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const hasContext = context !== undefined && context !== null && context !== false;
    const contextVisible = hasContext && (contextMode === 'drawer' ? drawerOpen : contextOpen);
    // Close the drawer when the screen widens: the column takes over.
    useEffect(() => {
        if (contextMode === 'inline')
            setDrawerOpen(false);
    }, [contextMode]);
    // The element focused last in each pane, to return to it.
    const lastFocus = useRef({});
    const paneOf = (target) => {
        const el = target instanceof HTMLElement ? target : null;
        const pane = el?.closest('[data-mn-pane]');
        if (!pane || !rootRef.current?.contains(pane))
            return null;
        return pane.getAttribute('data-mn-pane');
    };
    const onFocusCapture = (event) => {
        const pane = paneOf(event.target);
        const el = event.target;
        if (pane && !el.hasAttribute('data-mn-pane'))
            lastFocus.current[pane] = el;
    };
    const focusPane = useCallback((pane) => {
        const target = rootRef.current?.ownerDocument.getElementById(ids[pane]);
        if (!target || !rootRef.current?.contains(target))
            return;
        const last = lastFocus.current[pane];
        const el = last && last.isConnected && target.contains(last) ? last : target;
        el.focus({ preventScroll: el === target });
    }, [ids]);
    // One pane at a time: move focus with the visible pane so keyboard and
    // screen-reader users land in the content that just appeared.
    const prevDetailOpen = useRef(detailOpen);
    useEffect(() => {
        if (prevDetailOpen.current === detailOpen)
            return;
        prevDetailOpen.current = detailOpen;
        if (layout !== 'single')
            return;
        focusPane(detailOpen ? 'detail' : 'list');
    }, [detailOpen, layout, focusPane]);
    // The drawer has no Radix trigger, so remember what opened it and return there.
    const drawerReturn = useRef(null);
    const openDrawer = useCallback((open) => {
        if (open && typeof document !== 'undefined') {
            drawerReturn.current = document.activeElement;
        }
        setDrawerOpen(open);
    }, []);
    const setContextVisible = useCallback((visible) => {
        if (contextMode === 'drawer')
            openDrawer(visible);
        else
            setContextOpen(visible);
    }, [contextMode, openDrawer, setContextOpen]);
    const visiblePanes = () => {
        if (layout === 'single')
            return [detailOpen ? 'detail' : 'list'];
        const panes = [];
        if (!listCollapsed)
            panes.push('list');
        panes.push('detail');
        if (hasContext && contextMode === 'inline' && contextOpen)
            panes.push('context');
        return panes;
    };
    const handleKeyDown = (event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented || event.key !== 'F6')
            return;
        const panes = visiblePanes();
        if (panes.length < 2)
            return;
        const current = paneOf(event.target);
        const at = current ? panes.indexOf(current) : -1;
        const next = panes[(at + (event.shiftKey ? -1 : 1) + panes.length) % panes.length];
        if (!next)
            return;
        event.preventDefault();
        focusPane(next);
    };
    const state = useMemo(() => ({
        layout,
        contextMode,
        contextVisible,
        setContextVisible,
        toggleContext: () => setContextVisible(!contextVisible),
        listCollapsed,
        setListCollapsed,
        focusPane,
        ids,
    }), [
        layout,
        contextMode,
        contextVisible,
        setContextVisible,
        listCollapsed,
        setListCollapsed,
        focusPane,
        ids,
    ]);
    const showInlineContext = hasContext && contextMode === 'inline' && contextOpen;
    const vars = {
        '--_list': `${listSize}px`,
        '--_context': `${contextSize}px`,
        ...style,
    };
    return (_jsx(LayoutContext.Provider, { value: state, children: _jsxs("div", { ref: rootRef, ...rest, style: vars, "data-layout": layout, "data-detail-open": detailOpen || undefined, "data-list-collapsed": listCollapsed || undefined, "data-context-open": showInlineContext || undefined, className: cx('mn-split-inbox', className), onKeyDown: handleKeyDown, onFocusCapture: onFocusCapture, children: [_jsx("aside", { id: ids.list, "aria-label": listLabel, tabIndex: -1, "data-mn-pane": "list", className: "mn-split-inbox__pane mn-split-inbox__list", children: list }), resizable ? (_jsx(Splitter, { label: listResizeLabel, controls: ids.list, size: listSize, min: minListSize, max: maxListSize, step: resizeStep, collapsed: listCollapsed, edge: "start", onSize: setListSize, onToggle: () => setListCollapsed(!listCollapsed) })) : null, _jsxs("section", { id: ids.detail, "aria-label": detailLabel, tabIndex: -1, "data-mn-pane": "detail", className: "mn-split-inbox__pane mn-split-inbox__detail", children: [onBack ? (_jsx("div", { className: "mn-split-inbox__back", children: _jsx(Button, { variant: "ghost", size: "sm", leadingIcon: _jsx(ChevronRightGlyph, { className: "mn-split-inbox__back-glyph" }), onClick: onBack, children: backLabel }) })) : null, detail] }), showInlineContext ? (_jsxs(_Fragment, { children: [resizable ? (_jsx(Splitter, { label: contextResizeLabel, controls: ids.context, size: contextSize, min: minContextSize, max: maxContextSize, step: resizeStep, collapsed: false, edge: "end", onSize: setContextSize, onToggle: () => {
                                focusPane('detail');
                                setContextOpen(false);
                            } })) : null, _jsx("aside", { id: ids.context, "aria-label": contextLabel, tabIndex: -1, "data-mn-pane": "context", className: "mn-split-inbox__pane mn-split-inbox__context", children: context })] })) : null, hasContext && contextMode === 'drawer' ? (_jsx(Sheet, { open: drawerOpen, onOpenChange: openDrawer, children: _jsxs(SheetContent, { onCloseAutoFocus: (event) => {
                            const el = drawerReturn.current;
                            if (el && el.isConnected) {
                                event.preventDefault();
                                el.focus();
                            }
                        }, side: "right", size: "sm", closeLabel: contextCloseLabel, "aria-describedby": undefined, className: "mn-split-inbox__drawer", children: [_jsx(SheetHeader, { children: _jsx(SheetTitle, { children: contextLabel }) }), _jsx(SheetBody, { children: context })] }) })) : null] }) }));
});
//# sourceMappingURL=split-inbox-layout.js.map